import React, { useState } from 'react';
import posthog from 'posthog-js';
import { SportsEvent, SportsTeamData, SportsTeam } from '../types';
import {
  extractTeamAbbreviations,
  formatSportsVolume,
  formatGameTime,
  getTeamColor,
  getTeamAbbreviationFromName,
  getDisplayAbbreviation,
  getDisplayTeamName,
  isGameLive
} from '../utils/sportsUtils';
import './SportsEventCard.css';

interface SportsEventCardProps {
  event: SportsEvent;
  onTeamClick: (marketId: string, teamName: string, impliedProb: number, eventTitle: string, sportsTeamData: SportsTeamData) => void;
  isPinned?: boolean;
  onPinToggle?: (eventId: string) => void;
  parlayLegs?: Array<{
    eventTitle: string;
    marketQuestion: string;
    outcome: 'yes' | 'no';
    outcomePrice: number;
    decimalOdds: number;
    endDate?: string;
    marketIcon?: string;
    marketId: string;
    sportsTeamData?: any;
  }>;
}

const SportsEventCard: React.FC<SportsEventCardProps> = ({ event, onTeamClick, isPinned = false, onPinToggle, parlayLegs = [] }) => {
  const [hoveredOutcome, setHoveredOutcome] = useState<{ marketId: string; team: 1 | 2 | 0 } | null>(null);

  // Calculate estimated multiplier if this team was added to parlay
  const calculateEstimatedMultiplier = (impliedProb: number): string => {
    // Calculate existing multiplier from current parlay legs
    const existingMultiplier = parlayLegs.reduce((acc, leg) => acc * leg.decimalOdds, 1.0);

    // Calculate new odds (decimal odds = 1 / probability)
    const newOdds = impliedProb > 0 ? (1 / impliedProb) : 1.0;

    // Calculate total multiplier
    const totalMultiplier = existingMultiplier * newOdds;

    // Return formatted multiplier (e.g., "2.75")
    return totalMultiplier.toFixed(2);
  };

  const [team1AbbrRaw, team2AbbrRaw] = extractTeamAbbreviations(event.slug);
  const team1Abbr = getDisplayAbbreviation(team1AbbrRaw, event.league_id);
  const team2Abbr = getDisplayAbbreviation(team2AbbrRaw, event.league_id);
  const isCFB = event.league_id.startsWith('CFB');
  const isNBA = event.league_id.startsWith('NBA');
  const isEPL = event.league_id === 'EPL';
  const isLaLiga = event.league_id === 'LA-LIGA';
  const isBundesliga = event.league_id === 'BUNDESLIGA';
  const isSerieA = event.league_id === 'SERIE-A';
  const isLigue1 = event.league_id === 'LIGUE-1';
  const isMLS = event.league_id === 'MLS';
  const isFIFAFriendlies = event.league_id === 'FIFA-FRIENDLIES';
  
  // Check if this is a 3-way soccer market
  const is3WaySoccer = isEPL || isLaLiga || isBundesliga || isSerieA || isLigue1 || isMLS || isFIFAFriendlies;
  
  // Check if this league uses team logos from database (not API icons)
  // All 3-way soccer leagues (EPL, La Liga, Bundesliga, Serie A, Ligue 1) + MLS + FIFA Friendlies + CFB use team logos
  const usesTeamLogos = isCFB || is3WaySoccer || isMLS || isFIFAFriendlies;
  
  // For 3-way soccer leagues and CFB, teams are in order. For others, match by name
  let team1: SportsTeam | null = null;
  let team2: SportsTeam | null = null;
  let drawTeam: SportsTeam | null = null;
  
  if (is3WaySoccer) {
    // 3-way soccer leagues have 3 outcomes: Team1, Draw, Team2 (in order)
    team1 = event.teams[0] || null;
    drawTeam = event.teams[1] || null;
    team2 = event.teams[2] || null;
  } else if (isCFB) {
    // For CFB, just use teams in order since we have logos
    team1 = event.teams[0] || null;
    team2 = event.teams[1] || null;
  } else if (isNBA) {
    // For NBA, match by team name or handle cases where team_name is already an abbreviation
    for (const team of event.teams) {
      const abbr = getTeamAbbreviationFromName(team.team_name, event.league_id);
      const teamNameUpper = team.team_name.toUpperCase();

      // Match by abbreviation from name lookup, or if team_name is already an abbreviation (e.g., "PHO")
      if (abbr === team1Abbr || teamNameUpper === team1AbbrRaw || teamNameUpper === team1Abbr) {
        team1 = team;
      } else if (abbr === team2Abbr || teamNameUpper === team2AbbrRaw || teamNameUpper === team2Abbr) {
        team2 = team;
      }
    }
  } else {
    // For other leagues, match by team name
    for (const team of event.teams) {
      const abbr = getTeamAbbreviationFromName(team.team_name, event.league_id);
      if (abbr === team1Abbr) {
        team1 = team;
      } else if (abbr === team2Abbr) {
        team2 = team;
      }
    }
  }

  // Removed noisy view capture

  if (!team1 || !team2) {
    return null;
  }

  const team1Color = getTeamColor(team1AbbrRaw, event.league_id);
  const team2Color = getTeamColor(team2AbbrRaw, event.league_id);

  const handleTeamClick = (teamName: string, impliedProb: number, selectedTeam: 1 | 2) => {
    // Non-null assertion safe here because we return early if team1 or team2 is null
    const sportsTeamData: SportsTeamData = {
      team1: {
        name: team1!.team_name,
        abbr: team1Abbr,
        color: team1Color,
        prob: team1!.implied_prob,
        logo: (usesTeamLogos ? event.team1_logo : (is3WaySoccer ? team1!.icon : undefined))
      },
      team2: {
        name: team2!.team_name,
        abbr: team2Abbr,
        color: team2Color,
        prob: team2!.implied_prob,
        logo: (usesTeamLogos ? event.team2_logo : (is3WaySoccer ? team2!.icon : undefined))
      },
      selectedTeam,
      eventImage: event.image,
    };
    try {
      posthog.capture('engagement:market_selected', {
        source: 'sports_event_card',
        market_id: event.market_id,
        market_question: event.question,
        outcome: selectedTeam === 1 ? 'yes' : 'no',
        outcome_price: impliedProb,
        event_title: event.title,
      });
    } catch {}
    onTeamClick(event.market_id, teamName, impliedProb, event.title, sportsTeamData);
  };

  return (
    <div className="sports-event-card">
      <div className="sports-card-header">
        <div className="sports-card-info">
          <span className="sports-game-time">
            {isGameLive(event) ? (
              <><span className="sports-live-text">Live</span><span className="sports-live-dot"></span></>
            ) : (
              formatGameTime(event.game_start_time)
            )}
          </span>
          <span className="sports-volume">{formatSportsVolume(event.volume)}</span>
        </div>
        <div className="sports-card-actions">
          {/* <span className="sports-game-view-link">Game view &gt;</span> */}
          {/* <span className="sports-notification-icon">🔔</span> */}
          {onPinToggle && (
            <button 
              className="sports-pin-button"
              onClick={() => onPinToggle(event.id)}
              title={isPinned ? "Unpin game" : "Pin game"}
            >
              {isPinned ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-pin-fill" viewBox="0 0 16 16">
                  <path d="M4.146.146A.5.5 0 0 1 4.5 0h7a.5.5 0 0 1 .5.5c0 .68-.342 1.174-.646 1.479-.126.125-.25.224-.354.298v4.431l.078.048c.203.127.476.314.751.555C12.36 7.775 13 8.527 13 9.5a.5.5 0 0 1-.5.5h-4v4.5c0 .276-.224 1.5-.5 1.5s-.5-1.224-.5-1.5V10h-4a.5.5 0 0 1-.5-.5c0-.973.64-1.725 1.17-2.189A6 6 0 0 1 5 6.708V2.277a3 3 0 0 1-.354-.298C4.342 1.674 4 1.179 4 .5a.5.5 0 0 1 .146-.354"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-pin" viewBox="0 0 16 16">
                  <path d="M4.146.146A.5.5 0 0 1 4.5 0h7a.5.5 0 0 1 .5.5c0 .68-.342 1.174-.646 1.479-.126.125-.25.224-.354.298v4.431l.078.048c.203.127.476.314.751.555C12.36 7.775 13 8.527 13 9.5a.5.5 0 0 1-.5.5h-4v4.5c0 .276-.224 1.5-.5 1.5s-.5-1.224-.5-1.5V10h-4a.5.5 0 0 1-.5-.5c0-.973.64-1.725 1.17-2.189A6 6 0 0 1 5 6.708V2.277a3 3 0 0 1-.354-.298C4.342 1.674 4 1.179 4 .5a.5.5 0 0 1 .146-.354m1.58 1.408-.002-.001zm-.002-.001.002.001A.5.5 0 0 1 6 2v5a.5.5 0 0 1-.276.447h-.002l-.012.007-.054.03a5 5 0 0 0-.827.58c-.318.278-.585.596-.725.936h7.792c-.14-.34-.407-.658-.725-.936a5 5 0 0 0-.881-.61l-.002-.006h-.002A.5.5 0 0 1 10 7V2a.5.5 0 0 1 .295-.458 1.8 1.8 0 0 0 .351-.271c.08-.08.155-.17.214-.271H5.14q.091.15.214.271a1.8 1.8 0 0 0 .37.282"/>
                </svg>
              )}
            </button>
          )}
        </div>
      </div>

      <div className="sports-teams-section">
        <div className="sports-team-row">
          {(usesTeamLogos && event.team1_logo) || (is3WaySoccer && team1.icon) ? (
            <div className={`sports-team-logo-image ${isFIFAFriendlies ? 'fifa-friendlies' : ''}`}>
              <img src={(usesTeamLogos ? event.team1_logo : team1.icon) || ''} alt={team1Abbr} />
            </div>
          ) : (
            <div className="sports-team-logo" style={{ background: team1Color, borderColor: team1Color }}>
              <span className="sports-team-abbr">{team1Abbr}</span>
            </div>
          )}
          <span className="sports-team-name">
            {getDisplayTeamName(team1!.team_name, event.league_id)}
            {event.team1_record && (
              <span className="sports-team-record"> {event.team1_record}</span>
            )}
          </span>
        </div>
        <div className="sports-team-row-with-buttons">
          <div className="sports-team-info">
            {(usesTeamLogos && event.team2_logo) || (is3WaySoccer && team2.icon) ? (
              <div className={`sports-team-logo-image ${isFIFAFriendlies ? 'fifa-friendlies' : ''}`}>
                <img src={(usesTeamLogos ? event.team2_logo : team2.icon) || ''} alt={team2Abbr} />
              </div>
            ) : (
              <div className="sports-team-logo" style={{ background: team2Color, borderColor: team2Color }}>
                <span className="sports-team-abbr">{team2Abbr}</span>
              </div>
            )}
            <span className="sports-team-name">
              {getDisplayTeamName(team2!.team_name, event.league_id)}
              {event.team2_record && (
                <span className="sports-team-record"> {event.team2_record}</span>
              )}
            </span>
          </div>
          <div className={`sports-betting-buttons ${is3WaySoccer ? 'three-way' : 'two-way'}`}>
            <button
              className="sports-bet-button"
              style={{ background: team1Color }}
              onClick={() => handleTeamClick(team1!.team_name, team1!.implied_prob, 1)}
              onMouseEnter={() => setHoveredOutcome({ marketId: event.market_id, team: 1 })}
              onMouseLeave={() => setHoveredOutcome(null)}
            >
              <span className="bet-team-abbr">{team1Abbr}</span>{' '}
              <span className="bet-probability">{Math.round(team1!.implied_prob * 100)}%</span>
              {hoveredOutcome?.marketId === event.market_id && hoveredOutcome?.team === 1 && parlayLegs.length >= 1 && (
                <span className="parlay-estimate-tooltip">
                  Estimated parlay odds if added: {calculateEstimatedMultiplier(team1!.implied_prob)}x
                </span>
              )}
            </button>
            {is3WaySoccer && drawTeam && (
              <button
                className="sports-bet-button sports-bet-button-draw"
                onClick={() => {
                  // Draw bet: include team1 and team2 data for logo display
                  const drawSportsTeamData: SportsTeamData = {
                    team1: {
                      name: team1!.team_name,
                      abbr: team1Abbr,
                      color: team1Color,
                      prob: team1!.implied_prob,
                      logo: (usesTeamLogos ? event.team1_logo : (is3WaySoccer ? team1!.icon : undefined))
                    },
                    team2: {
                      name: team2!.team_name,
                      abbr: team2Abbr,
                      color: team2Color,
                      prob: team2!.implied_prob,
                      logo: (usesTeamLogos ? event.team2_logo : (is3WaySoccer ? team2!.icon : undefined))
                    },
                    selectedTeam: 0,
                    eventImage: event.image,
                  };
                  onTeamClick(
                    event.market_id,
                    drawTeam!.team_name,
                    drawTeam!.implied_prob,
                    event.title,
                    drawSportsTeamData
                  );
                }}
                onMouseEnter={() => setHoveredOutcome({ marketId: event.market_id, team: 0 })}
                onMouseLeave={() => setHoveredOutcome(null)}
              >
                <span className="bet-team-abbr">DRAW</span>{' '}
                <span className="bet-probability">{Math.round(drawTeam!.implied_prob * 100)}%</span>
                {hoveredOutcome?.marketId === event.market_id && hoveredOutcome?.team === 0 && parlayLegs.length >= 1 && (
                  <span className="parlay-estimate-tooltip">
                    Estimated parlay odds if added: {calculateEstimatedMultiplier(drawTeam!.implied_prob)}x
                  </span>
                )}
              </button>
            )}
            <button
              className="sports-bet-button"
              style={{ background: team2Color }}
              onClick={() => handleTeamClick(team2!.team_name, team2!.implied_prob, 2)}
              onMouseEnter={() => setHoveredOutcome({ marketId: event.market_id, team: 2 })}
              onMouseLeave={() => setHoveredOutcome(null)}
            >
              <span className="bet-team-abbr">{team2Abbr}</span>{' '}
              <span className="bet-probability">{Math.round(team2!.implied_prob * 100)}%</span>
              {hoveredOutcome?.marketId === event.market_id && hoveredOutcome?.team === 2 && parlayLegs.length >= 1 && (
                <span className="parlay-estimate-tooltip">
                  Estimated parlay odds if added: {calculateEstimatedMultiplier(team2!.implied_prob)}x
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SportsEventCard;

